import React, { useState, useEffect } from "react";
import { conversionMap } from "../../constants";
import { toast } from "react-toastify";
import Timeline from "./Timeline";
import api from "../api";

const LeadDetails = ({ selectedLead, onLeadUpdate }) => {
  const [contacted, setContacted] = useState(selectedLead?.contacted || false);
  const [dealStatus, setDealStatus] = useState(
    selectedLead?.dealStatus || "open"
  );
  const [isUpdating, setIsUpdating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedLead, setEditedLead] = useState(selectedLead || {});
  const [activeTab, setActiveTab] = useState("details");

  // Update local state when selectedLead changes
  useEffect(() => {
    if (selectedLead) {
      setContacted(selectedLead.contacted || false);
      setDealStatus(selectedLead.dealStatus || "open");
      setEditedLead(selectedLead);
    }
  }, [selectedLead]);

  const updateDealStatus = async (contactedValue, statusValue = null) => {
    setIsUpdating(true);

    // Optimistic UI update - update immediately for better UX
    const newStatus = contactedValue ? statusValue || "open" : "open";
    const optimisticUpdate = {
      ...selectedLead,
      contacted: contactedValue,
      dealStatus: newStatus,
    };

    // Immediately update UI
    setContacted(contactedValue);
    setDealStatus(contactedValue ? newStatus : "open");

    // Update parent component immediately (optimistic update)
    if (onLeadUpdate) {
      onLeadUpdate(optimisticUpdate);
    }

    try {
      const requestBody = { contacted: contactedValue };

      // Scenario handling based on API guide:
      if (contactedValue) {
        // Scenarios 1, 2, 6: Initial contact with status or defaults to "open"
        if (statusValue) {
          requestBody.dealStatus = statusValue;
        } else {
          // Scenario 6: Contact with null status (defaults to "open")
          requestBody.dealStatus = "open";
        }
      } else {
        // Scenario 5: Reset deal status
        requestBody.dealStatus = "open";
      }

      await api.updateLeadContact(selectedLead.id, requestBody);

      // API succeeded - show success message
      toast.success("Deal status updated successfully", {
        containerId: "main-toast-container",
      });
    } catch (error) {
      // API failed or network error - revert optimistic update
      setContacted(selectedLead.contacted || false);
      setDealStatus(selectedLead.dealStatus || "open");

      // Revert parent component update
      if (onLeadUpdate) {
        onLeadUpdate(selectedLead);
      }

      toast.error(error.message || "Failed to update deal status", {
        containerId: "main-toast-container",
      });

      toast.error("Error updating deal status", {
        containerId: "main-toast-container",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const updateDealStatusOnly = async (status) => {
    // Scenarios 3, 4: Follow-up - update status only (when already contacted)
    if (!contacted) return; // Don't update status if not contacted

    setIsUpdating(true);

    // Optimistic UI update
    const optimisticUpdate = {
      ...selectedLead,
      dealStatus: status,
    };

    // Immediately update UI
    setDealStatus(status);

    // Update parent component immediately (optimistic update)
    if (onLeadUpdate) {
      onLeadUpdate(optimisticUpdate);
    }

    try {
      await api.updateLeadContact(selectedLead.id, { dealStatus: status });

      // API succeeded - show success message
      toast.success("Deal status updated successfully", {
        containerId: "main-toast-container",
      });
    } catch (error) {
      // API failed or network error - revert optimistic update
      setDealStatus(selectedLead.dealStatus || "open");

      // Revert parent component update
      if (onLeadUpdate) {
        onLeadUpdate(selectedLead);
      }

      toast.error(error.message || "Failed to update deal status", {
        containerId: "main-toast-container",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleContactedChange = (e) => {
    const isChecked = e.target.checked;
    if (isChecked) {
      // When checking contacted, default to "contacted" status
      updateDealStatus(isChecked, "contacted");
    } else {
      // When unchecking contacted, reset everything
      updateDealStatus(isChecked);
    }
  };

  const handleDealStatusChange = (e) => {
    const newStatus = e.target.value;
    updateDealStatusOnly(newStatus);
  };

  const handleEditToggle = () => {
    if (isEditing) {
      // Save changes
      saveLeadChanges();
    } else {
      setIsEditing(true);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setEditedLead((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const saveLeadChanges = async () => {
    setIsUpdating(true);

    // Optimistic UI update - immediately update the parent component
    if (onLeadUpdate) {
      onLeadUpdate(editedLead);
    }

    try {
      await api.request(`/lead/${selectedLead.id}`, {
        method: "PUT",
        body: editedLead,
      });

      // API succeeded - finalize the edit mode
      setIsEditing(false);

      toast.success("Lead updated successfully", {
        containerId: "main-toast-container",
      });
    } catch (error) {
      // API failed or network error - revert optimistic update
      if (onLeadUpdate) {
        onLeadUpdate(selectedLead);
      }

      toast.error(error.message || "Failed to update lead", {
        containerId: "main-toast-container",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditedLead(selectedLead);
  };
  // Group fields by category - include ALL schema fields
  const fieldCategories = {
    "Contact Information": ["company", "name", "email", "phone"],
    "Equipment Details": [
      "brandMake",
      "model",
      "quantity",
      "primaryUser",
      "hoursGotIt",
    ],
    "Technical Information": [
      "mainIssue",
      "oemPartNumber",
      "wetOrDryApplication",
    ],
    "Rebuild Information": ["rebuildOnSite", "whoRebuild"],
    "Business Interests": [
      "interestedInNewMaterial",
      "needExpertOpinion",
      "openToNewApplication",
    ],
    "Financial Impact": [
      "hoursOfDowntime",
      "amountLossByThroughputHr",
      "mattersMost",
      "costExistingPart",
      "totalSavings",
    ],
    "Operational Metrics": [
      "shiftsPerDay",
      "weeksPerMonth",
      "monthsPerYear",
      "partsPerEquipment",
      "operativeDaysPerWeek",
    ],
    "Lead Management": [
      "score",
      "notes",
      "source",
      "contacted",
      "contactStatus",
      "contactedAt",
      "contactedBy",
    ],
    "System Information": ["createdAt", "updatedAt", "isActive"],
  };

  const renderFieldValue = (key, value) => {
    if (
      value === null ||
      value === undefined ||
      value === "" ||
      (typeof value === "string" && value.trim() === "")
    ) {
      return <span className="text-gray-400 italic">Not provided</span>;
    }

    // Format date fields
    if (
      key === "createdAt" ||
      key === "updatedAt" ||
      key === "created_at" ||
      key === "updated_at" ||
      key === "contactedAt" ||
      key === "contacted_at"
    ) {
      return new Date(value).toLocaleString();
    }

    // Format contactedBy field to show admin name
    if (key === "contactedBy") {
      // Check if we have the admin name available
      const adminName =
        selectedLead?.contactedByName || editedLead?.contactedByName;
      if (adminName) {
        return adminName;
      }
      // Fallback to showing "Admin" if we only have the ID
      return value ? "Admin" : "Not provided";
    }

    // Format boolean fields
    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }

    // Format currency fields
    if (
      (key === "totalSavings" ||
        key === "total_savings" ||
        key === "costExistingPart" ||
        key === "cost_existing_part" ||
        key === "amountLossByThroughputHr" ||
        key === "amount_loss_by_throughput_hr") &&
      typeof value === "number"
    ) {
      return value.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    }

    // Format hour fields
    if (
      (key === "hoursGotIt" ||
        key === "hours_got_it" ||
        key === "hoursOfDowntime" ||
        key === "hours_of_downtime") &&
      typeof value === "number"
    ) {
      return `${value.toLocaleString()} hours`;
    }

    // Format numeric fields
    if (
      (key === "shiftsPerDay" ||
        key === "shifts_per_day" ||
        key === "partsPerEquipment" ||
        key === "parts_per_equipment" ||
        key === "operativeDaysPerWeek" ||
        key === "operative_days_per_week" ||
        key === "quantity" ||
        key === "score") &&
      typeof value === "number"
    ) {
      return value.toLocaleString();
    }

    return value?.toString();
  };

  const renderEditableField = (key, value) => {
    if (
      key === "contacted" ||
      key === "contactStatus" ||
      key === "contactedAt" ||
      key === "contactedBy"
    ) {
      return renderFieldValue(key, value); // These are handled separately
    }

    const fieldType = typeof value;

    if (fieldType === "boolean") {
      return (
        <select
          value={value ? "true" : "false"}
          onChange={(e) => handleFieldChange(key, e.target.value === "true")}
          className="w-full border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:border-gray-900"
        >
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      );
    }

    if (fieldType === "number") {
      return (
        <input
          type="number"
          value={value || ""}
          onChange={(e) =>
            handleFieldChange(key, parseFloat(e.target.value) || 0)
          }
          className="w-full border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:border-gray-900"
        />
      );
    }

    // For select fields, we need to check known enums
    const selectOptions = {
      primaryUser: [
        "I am the primary user",
        "Someone else",
        "Shared responsibility",
      ],
      mainIssue: [
        "D-Bonding",
        "Contamination",
        "Dust",
        "Debree",
        "Heat",
        "Low Hours",
      ],
      wetOrDryApplication: ["Wet", "Dry", "Both"],
      mattersMost: ["Durability", "Availability", "Price"],
    };

    if (selectOptions[key]) {
      return (
        <select
          value={value || ""}
          onChange={(e) => handleFieldChange(key, e.target.value)}
          className="w-full border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:border-gray-900"
        >
          <option value="">Select...</option>
          {selectOptions[key].map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
    }

    // For notes field, use textarea
    if (key === "notes") {
      return (
        <textarea
          value={value || ""}
          onChange={(e) => handleFieldChange(key, e.target.value)}
          className="w-full border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:border-gray-900"
          rows={3}
        />
      );
    }

    // Default to text input
    return (
      <input
        type="text"
        value={value || ""}
        onChange={(e) => handleFieldChange(key, e.target.value)}
        className="w-full border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:border-gray-900"
      />
    );
  };

  return (
    <div>
      {/* Header with Edit Controls */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-800">Lead Details</h2>
        <div className="flex space-x-2">
          {isEditing ? (
            <>
              <button
                onClick={cancelEdit}
                disabled={isUpdating}
                className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleEditToggle}
                disabled={isUpdating}
                className="px-4 py-2 text-sm bg-black text-white rounded-md hover:bg-gray-800 disabled:opacity-50"
              >
                {isUpdating ? "Saving..." : "Save Changes"}
              </button>
            </>
          ) : (
            <button
              onClick={handleEditToggle}
              className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
            >
              Edit Lead
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab("details")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "details"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Lead Details
          </button>
          <button
            onClick={() => setActiveTab("timeline")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "timeline"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Timeline
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "details" && (
        <>
          {/* Contact Status Controls */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6 border">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">
              Contact Management
            </h3>

            {/* Helper text */}
            <div className="mb-3 text-sm text-gray-600">
              {!contacted &&
                "Check 'Contacted' to mark this lead as contacted (defaults to Contacted status)"}
              {contacted &&
                dealStatus === "open" &&
                "Deal is open - ready to be contacted."}
              {contacted &&
                dealStatus === "contacted" &&
                "Lead has been contacted - follow up needed."}
              {contacted &&
                dealStatus === "interested" &&
                "Lead is interested - opportunity to move forward."}
              {contacted &&
                dealStatus === "quoted" &&
                "Quote provided - awaiting response."}
              {contacted &&
                (dealStatus === "won" || dealStatus === "closed won") &&
                "Deal won! ✅ Successful conversion."}
              {contacted &&
                (dealStatus === "lost" || dealStatus === "closed lost") &&
                "Deal lost. Consider following up later."}
              {contacted &&
                dealStatus === "no response" &&
                "No response received from this lead."}
            </div>

            <div className="flex items-center space-x-6">
              {/* Contacted Checkbox */}
              <div className="flex items-center">
                <input
                  id="contacted-checkbox"
                  type="checkbox"
                  checked={contacted}
                  onChange={handleContactedChange}
                  disabled={isUpdating}
                  className="h-4 w-4 text-black focus:ring-black border-gray-300 rounded disabled:opacity-50"
                />
                <label
                  htmlFor="contacted-checkbox"
                  className="ml-2 text-sm font-medium text-gray-700"
                >
                  Contacted
                </label>
              </div>

              {/* Deal Status Select */}
              <div className="flex items-center space-x-2">
                <label
                  htmlFor="deal-status"
                  className="text-sm font-medium text-gray-700"
                >
                  Deal Status:
                </label>
                <select
                  id="deal-status"
                  value={dealStatus}
                  onChange={handleDealStatusChange}
                  disabled={!contacted || isUpdating}
                  className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:border-gray-900 disabled:opacity-50 disabled:bg-gray-100"
                >
                  <option value="open">Open</option>
                  <option value="contacted">Contacted</option>
                  <option value="interested">Interested</option>
                  <option value="quoted">Quoted</option>
                  <option value="won">Won</option>
                  <option value="lost">Lost</option>
                  <option value="closed won">Closed Won</option>
                  <option value="closed lost">Closed Lost</option>
                  <option value="no response">No Response</option>
                </select>
              </div>

              {/* Loading indicator */}
              {isUpdating && (
                <div className="flex items-center text-sm text-gray-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900 mr-2"></div>
                  Updating...
                </div>
              )}
            </div>
          </div>

          {/* Lead Details by Category */}
          <div className="space-y-6 max-h-96 overflow-y-auto">
            {Object.entries(fieldCategories).map(([categoryName, fields]) => {
              // Show all fields for each category, including those without values
              const categoryFields = fields;

              return (
                <div
                  key={categoryName}
                  className="bg-white border rounded-lg p-4"
                >
                  <h4 className="text-lg font-semibold mb-3 text-gray-800 border-b pb-2">
                    {categoryName}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {categoryFields.map((field) => {
                      const value = isEditing
                        ? editedLead[field]
                        : selectedLead[field];
                      const label = conversionMap[field] || field;

                      // Show fields even if they don't have values, but mark them as missing
                      const hasValue =
                        value !== null &&
                        value !== undefined &&
                        value !== "" &&
                        !(typeof value === "string" && value.trim() === "");

                      return (
                        <div key={field} className="space-y-1">
                          <label className="text-sm font-medium text-gray-700">
                            {label}
                            {field === "openToNewApplication" &&
                              value === true && (
                                <span className="ml-2 text-xs bg-amber-200 text-amber-800 px-2 py-1 rounded-full">
                                  Priority Lead
                                </span>
                              )}
                            {!hasValue && !isEditing && (
                              <span className="ml-2 text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">
                                Missing
                              </span>
                            )}
                          </label>
                          <div
                            className={`text-sm ${
                              field === "openToNewApplication" && value === true
                                ? "text-amber-900 font-medium"
                                : hasValue
                                ? "text-gray-900"
                                : "text-gray-400"
                            }`}
                          >
                            {isEditing &&
                            categoryName !== "Contact Management" &&
                            categoryName !== "System Information"
                              ? renderEditableField(field, value)
                              : renderFieldValue(field, value)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Timeline Tab Content */}
      {activeTab === "timeline" && (
        <div className="bg-white rounded-lg border p-6">
          <Timeline
            leadId={selectedLead?.id}
            leadName={selectedLead?.name}
            leadCompany={selectedLead?.company}
          />
        </div>
      )}
    </div>
  );
};

export default LeadDetails;
